import { TooltipTitle } from './TooltipTitle';
import { useEffect, useRef, useState } from 'react';
import { Database, Expand, ImageOff } from 'lucide-react';
import type { DocumentResult, FieldResult } from '../shared/desktop-api';
import {
  fieldSourceCropBounds,
  resolveFieldSource,
  sourcePageIdentity,
} from './field-source-geometry';
import {
  sourcePreviewCache as previewCache,
  watchSourcePreview,
  type SourcePreview,
} from './field-source-preview-cache';
import './field-source-crop.css';
import { WorkspaceDialog } from './WorkspaceDialog';

/** Read-only source companion. Cropping changes the viewport, never page pixels,
 * recognized values, drafts, confirmations, or evidence indexes. */
export function FieldSourceCrop({
  document,
  field,
  documentMutationToken,
  onShowPage,
}: {
  document: DocumentResult;
  field: FieldResult | undefined;
  documentMutationToken: string;
  onShowPage?: (pageNumber: number) => void;
}) {
  const element = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ identity: '', x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [expandedIdentity, setExpandedIdentity] = useState<string | null>(null);
  const drag = useRef<{
    identity: string;
    pointer: number;
    x: number;
    y: number;
    originX: number;
    originY: number;
    scale: number;
  } | null>(null);
  const dragged = useRef(false);
  const [visibleIdentity, setVisibleIdentity] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<{
    identity: string;
    preview?: SourcePreview;
    failed?: true;
  }>();
  const evidence = resolveFieldSource(document, field, documentMutationToken);
  const identity = evidence.kind === 'source' ? evidence.identity : '';
  const page = evidence.kind === 'source' ? evidence.page : undefined;
  // Operators navigate within this document. Stored page numbers identify the
  // batch's source stream and must not make a two-page document say "page 11".
  const documentPage = page ? document.pages.indexOf(page) + 1 : undefined;
  useEffect(() => {
    if (!element.current || !identity) return;
    if (typeof IntersectionObserver === 'undefined') return; // No speculative page load.
    let current = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (current)
          setVisibleIdentity(
            entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)
              ? identity
              : null,
          );
      },
      { threshold: 0 },
    );
    observer.observe(element.current);
    return () => {
      current = false;
      observer.disconnect();
    };
  }, [identity]);
  useEffect(() => {
    // Per-field state must release the original image too; a bounded shared LRU
    // alone does not bound images retained by hundreds of scrolled-away fields.
    setLoaded(undefined);
    if (visibleIdentity !== identity || !page || !identity) return;
    return watchSourcePreview(
      previewCache,
      sourcePageIdentity(document.id, documentMutationToken, page),
      page.previewPath,
      (preview) => setLoaded({ identity, preview }),
      () => setLoaded({ identity, failed: true }),
    );
  }, [visibleIdentity, identity]);
  // Render admission, not just an effect cleanup: the old image cannot paint
  // for even one render when its document/revision/field/source has changed.
  const result = visibleIdentity === identity && loaded?.identity === identity ? loaded : undefined;
  const crop =
    evidence.kind === 'source' && result?.preview
      ? fieldSourceCropBounds(evidence, result.preview.width, result.preview.height)
      : null;
  const offset = pan.identity === identity ? pan : { x: 0, y: 0 };
  const state =
    evidence.kind === 'lookup'
      ? 'lookup'
      : evidence.kind === 'missing'
        ? 'missing'
        : result?.failed || (result?.preview && !crop)
          ? 'unavailable'
          : crop
            ? 'ready'
            : 'loading';
  const show = crop
    ? () => setExpandedIdentity(identity)
    : page && onShowPage
      ? () => onShowPage(page.number)
      : undefined;
  const name = field?.name ?? 'field';
  const content =
    crop && result?.preview ? (
      <>
        <svg
          role="img"
          aria-label={`Original page area cited for ${name}`}
          viewBox={`${crop.x + offset.x} ${crop.y + offset.y} ${crop.width} ${crop.height}`}
          preserveAspectRatio="xMidYMid meet"
          className={dragging ? 'panning' : undefined}
          onPointerDown={(event) => {
            if (event.button !== 0 || !event.isPrimary) return;
            const scale = event.currentTarget.getScreenCTM()?.a;
            if (!scale || scale <= 0) return;
            dragged.current = false;
            drag.current = {
              identity,
              pointer: event.pointerId,
              x: event.clientX,
              y: event.clientY,
              originX: offset.x,
              originY: offset.y,
              scale,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
            event.preventDefault();
          }}
          onPointerMove={(event) => {
            const active = drag.current;
            if (!active || active.identity !== identity || active.pointer !== event.pointerId)
              return;
            const dx = event.clientX - active.x,
              dy = event.clientY - active.y;
            if (!dragged.current && Math.hypot(dx, dy) < 4) return;
            dragged.current = true;
            setDragging(true);
            setPan({
              identity,
              x: Math.max(
                -crop.x,
                Math.min(
                  result.preview!.width - crop.width - crop.x,
                  active.originX - dx / active.scale,
                ),
              ),
              y: Math.max(
                -crop.y,
                Math.min(
                  result.preview!.height - crop.height - crop.y,
                  active.originY - dy / active.scale,
                ),
              ),
            });
            event.preventDefault();
          }}
          onPointerUp={(event) => {
            if (drag.current?.pointer === event.pointerId) {
              drag.current = null;
              setDragging(false);
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
          }}
          onPointerCancel={() => {
            drag.current = null;
            setDragging(false);
          }}
          onLostPointerCapture={() => {
            drag.current = null;
            setDragging(false);
          }}
        >
          <TooltipTitle>Click to enlarge</TooltipTitle>
          <image
            href={result.preview.source}
            x={0}
            y={0}
            width={result.preview.width}
            height={result.preview.height}
          />
        </svg>
        <span className="field-source-caption">
          Source · page {documentPage}
          {show && <Expand size={12} />}
        </span>
      </>
    ) : evidence.kind === 'lookup' ? (
      <span className="field-source-placeholder">
        <Database size={15} />
        <span>
          From lookup<small>{evidence.attribution}</small>
        </span>
      </span>
    ) : (
      <span className="field-source-placeholder">
        <ImageOff size={15} />
        <span>
          {evidence.kind === 'missing'
            ? evidence.reason
            : state === 'unavailable'
              ? 'Source preview unavailable'
              : 'Source preview'}
          {show && <small>Open full page</small>}
        </span>
      </span>
    );
  return (
    <div
      ref={element}
      className="field-source-crop"
      data-source-state={state}
      data-source-page-number={page?.number}
      data-source-document-page={documentPage}
    >
      {expandedIdentity === identity && crop && result?.preview && (
        <WorkspaceDialog
          telemetry="field-source"
          title={`${name} · source`}
          onClose={() => setExpandedIdentity(null)}
        >
          <div className="field-source-enlarged">{content}</div>
        </WorkspaceDialog>
      )}
      {show ? (
        <button
          type="button"
          aria-label={`Show ${name} source on page ${documentPage}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            if (dragged.current) {
              dragged.current = false;
              return;
            }
            show();
          }}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
}
